import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import api from '../services/api';
import { 
  Users, 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  Save, 
  ChevronLeft, 
  ChevronRight, 
  Edit3, 
  GraduationCap, 
  LayoutGrid
} from 'lucide-react';
import StudentAvatar from '../components/StudentAvatar';

const Attendance = () => {
  const { classId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialDate = queryParams.get('date') || new Date().toISOString().split('T')[0];

  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({}); // { studentId: { status, remarks } }
  const [date, setDate] = useState(initialDate);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [classStatus, setClassStatus] = useState('Active');
  
  const [showCalendar, setShowCalendar] = useState(false);
  const [activeMonth, setActiveMonth] = useState(new Date(initialDate).toLocaleString('en-US', { month: 'long', year: 'numeric' }));

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await api.get('/classes');
        setClasses(res.data);
        
        if (classId) {
          const found = res.data.find(c => c.id === parseInt(classId));
          if (found) setSelectedClass(found);
          else if (res.data.length > 0) setSelectedClass(res.data[0]);
        } else if (res.data.length > 0) {
          setSelectedClass(res.data[0]);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchClasses();
  }, [classId]);

  const fetchStudentsAndAttendance = useCallback(async () => {
    if (!selectedClass) return;
    try {
      setLoading(true);
      const attendanceRes = await api.get(`/classes/${selectedClass.id}/attendance?date=${date}`);
      const attendanceData = attendanceRes.data;
      
      const existingStudents = attendanceData.map(a => ({
        id: a.student_id,
        full_name: a.full_name,
        phone: a.phone,
        photo_url: a.photo_url,
        target_country: a.target_country,
        application_status: a.application_status
      }));
      
      setStudents(existingStudents);
      
      const initialAttendance = {};
      attendanceData.forEach(a => {
        initialAttendance[a.student_id] = {
          status: a.status,
          remarks: a.remarks || ''
        };
      });
      if (attendanceData.length > 0) {
        setClassStatus(attendanceData[0].class_status);
      } else if (selectedClass) {
        setClassStatus(selectedClass.status);
      }
      
      setAttendance(initialAttendance);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedClass, date]);

  useEffect(() => {
    fetchStudentsAndAttendance();
  }, [fetchStudentsAndAttendance]);

  // Calendar Logic
  const calendarGrid = useMemo(() => {
    const [monthName, yearStr] = activeMonth.split(' ');
    const monthIdx = new Date(Date.parse(monthName + " 1, 2012")).getMonth();
    const year = parseInt(yearStr);
    const firstDay = new Date(year, monthIdx, 1).getDay();
    const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
    const grid = [];
    let day = 1;
    for (let i = 0; i < 6; i++) {
      const week = [];
      for (let j = 0; j < 7; j++) {
        if ((i === 0 && j < firstDay) || day > daysInMonth) week.push(null);
        else {
          const dateStr = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          week.push(dateStr);
          day++;
        }
      }
      grid.push(week);
      if (day > daysInMonth) break;
    }
    return grid;
  }, [activeMonth]);

  const changeMonth = (offset) => {
    const [m, y] = activeMonth.split(' ');
    const d = new Date(Date.parse(m + " 1, " + y));
    d.setMonth(d.getMonth() + offset);
    setActiveMonth(d.toLocaleString('en-US', { month: 'long', year: 'numeric' }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const attendanceRecords = Object.keys(attendance).map(studentId => ({
        studentId: parseInt(studentId),
        status: attendance[studentId].status,
        remarks: attendance[studentId].remarks
      }));

      await api.post('/classes/attendance', {
        classId: selectedClass.id,
        date,
        attendanceRecords
      });
      alert('Attendance saved successfully!');
    } catch (err) {
      alert('Error saving attendance');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (studentId, status) => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: { ...prev[studentId], status }
    }));
  };

  const handleMarkAllPresent = () => {
    const allPresent = {};
    students.forEach(s => {
      allPresent[s.id] = { status: 'Present', remarks: attendance[s.id]?.remarks || '' };
    });
    setAttendance(prev => ({ ...prev, ...allPresent }));
  };

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8 animate-in fade-in duration-700">
      
      {/* Header with Stats */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6">
        <div>
          <h1 className="text-4xl font-black text-gray-900 tracking-tighter">Class Attendance</h1>
          <p className="text-sm font-bold text-gray-400 uppercase tracking-[0.3em] mt-2">Daily Class Attendance</p>
        </div>
        
        <div className="flex flex-wrap gap-3">
          {[
            { label: 'Enrolled', val: students.length, icon: GraduationCap, bg: 'bg-indigo-50', color: 'text-indigo-600' },
            { label: 'Present', val: Object.values(attendance).filter(a => a.status === 'Present').length, icon: CheckCircle2, bg: 'bg-emerald-50', color: 'text-emerald-600' },
            { label: 'Absent', val: Object.values(attendance).filter(a => a.status === 'Absent').length, icon: XCircle, bg: 'bg-rose-50', color: 'text-rose-600' },
          ].map((s, i) => (
            <div key={i} className={`${s.bg} px-6 py-3 rounded-2xl border border-white shadow-sm flex items-center gap-4`}>
              <div className={`${s.color}`}>
                <s.icon size={20} />
              </div>
              <div className="text-left">
                <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 leading-none">{s.label}</p>
                <h4 className={`text-xl font-black ${s.color}`}>{s.val}</h4>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Class Selection Row */}
      {!classId && (
        <div className="bg-white p-4 rounded-[2rem] border border-gray-100 shadow-xl shadow-indigo-900/5">
          <div className="flex items-center gap-2 mb-3">
            <LayoutGrid size={13} className="text-gray-300" />
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Class</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {classes.map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedClass(c)}
                className={`px-4 py-2.5 rounded-xl transition-all border ${
                  selectedClass?.id === c.id
                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-xl shadow-indigo-100'
                    : 'bg-gray-50 border-transparent hover:border-gray-200 text-gray-600'
                }`}
              >
                <span className="font-black text-xs uppercase tracking-tight">{c.name}</span>
                <span className={`text-[9px] font-bold ml-2 opacity-70 ${selectedClass?.id === c.id ? 'text-indigo-100' : 'text-gray-400'}`}>
                  {c.time_slot || 'Various Slots'}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div className="bg-white rounded-[3rem] border border-gray-100 shadow-xl shadow-indigo-900/5 overflow-hidden">
            {/* Table Controls */}
            <div className="p-8 border-b border-gray-50 flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-xl shadow-indigo-100">
                   <Users size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-gray-900 tracking-tight">{selectedClass?.name || 'Loading Class...'}</h2>
                  <p className="text-xs font-black text-indigo-400 uppercase tracking-widest">{new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {/* Inline Date Picker Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowCalendar(v => !v)}
                    className="flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-indigo-50 border border-gray-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-600 transition-all"
                  >
                    <ChevronLeft size={13} className="rotate-[-90deg] text-indigo-400" />
                    {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </button>
                  {showCalendar && (
                    <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-2xl border border-gray-100 shadow-2xl p-4 w-72">
                      <div className="flex items-center justify-between mb-3">
                        <button onClick={() => changeMonth(-1)} className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors"><ChevronLeft size={14} /></button>
                        <h4 className="text-xs font-black text-indigo-600 tracking-tight">{activeMonth}</h4>
                        <button onClick={() => changeMonth(1)} className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors"><ChevronRight size={14} /></button>
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                          <div key={i} className="text-center text-[9px] font-black text-gray-300 py-1">{d}</div>
                        ))}
                        {calendarGrid.flat().map((d, i) => (
                          <div key={i} className="aspect-square p-0.5">
                            {d && (
                              <button
                                onClick={() => { setDate(d); setShowCalendar(false); }}
                                className={`w-full h-full rounded-xl text-[11px] font-black transition-all ${
                                  date === d ? 'bg-indigo-600 text-white shadow-lg scale-110' : 'text-gray-500 hover:bg-gray-50'
                                } ${new Date().toISOString().split('T')[0] === d && date !== d ? 'text-indigo-600 ring-1 ring-indigo-100' : ''}`}
                              >
                                {parseInt(d.split('-')[2])}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button 
                  onClick={handleMarkAllPresent}
                  disabled={students.length === 0 || classStatus !== 'Active'}
                  className="px-6 py-3 bg-emerald-50 text-emerald-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 hover:text-white transition-all border border-emerald-100 disabled:opacity-30"
                >
                  Mark All Present
                </button>
                <button 
                  onClick={handleSave}
                  disabled={saving || loading || students.length === 0 || classStatus !== 'Active'}
                  className="px-8 py-3 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 flex items-center gap-2 disabled:opacity-30"
                >
                  <Save size={14} />
                  {saving ? 'Saving...' : 'Save Attendance'}
                </button>
              </div>
            </div>

            {/* Attendance List */}
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-gray-50/50 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em]">
                    <th className="px-8 py-5">Student Name</th>
                    <th className="px-8 py-5">Status</th>
                    <th className="px-8 py-5">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    <tr><td colSpan="3" className="p-20 text-center"><div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mx-auto"></div></td></tr>
                  ) : students.length === 0 ? (
                    <tr><td colSpan="3" className="p-20 text-center text-gray-400 font-bold uppercase tracking-widest text-[10px]">No students found for this class</td></tr>
                  ) : (
                    students.map(student => (
                      <tr key={student.id} className="hover:bg-indigo-50/20 transition-all group">
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-4">
                            <StudentAvatar photoUrl={student.photo_url} fullName={student.full_name} className="w-10 h-10 rounded-xl" />
                            <div>
                              <p className="text-sm font-black text-gray-900 group-hover:text-indigo-600 transition-colors">{student.full_name}</p>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">ID: #{student.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-2">
                            {[
                              { s: 'Present', i: CheckCircle2, c: 'bg-emerald-500 shadow-emerald-100' },
                              { s: 'Absent', i: XCircle, c: 'bg-rose-500 shadow-rose-100' },
                              { s: 'Late', i: AlertCircle, c: 'bg-amber-500 shadow-amber-100' }
                            ].map(btn => (
                              <button
                                key={btn.s}
                                onClick={() => handleStatusChange(student.id, btn.s)}
                                disabled={classStatus !== 'Active'}
                                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                                  attendance[student.id]?.status === btn.s 
                                    ? `${btn.c} text-white shadow-lg scale-105` 
                                    : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                                }`}
                              >
                                <btn.i size={14} />
                                {btn.s}
                              </button>
                            ))}
                          </div>
                        </td>
                        <td className="px-8 py-5">
                          <div className="relative group/remarks">
                            <input 
                              type="text" 
                              placeholder="Add notes..." 
                              disabled={classStatus !== 'Active'}
                              className="w-full bg-gray-50/50 hover:bg-white border-b-2 border-transparent focus:border-indigo-600 outline-none p-2 text-xs font-bold text-gray-600 transition-all rounded-lg"
                              value={attendance[student.id]?.remarks || ''}
                              onChange={e => setAttendance(prev => ({ ...prev, [student.id]: { ...prev[student.id], remarks: e.target.value } }))}
                            />
                            <Edit3 size={12} className="absolute right-2 top-2.5 text-gray-300 opacity-0 group-hover/remarks:opacity-100 transition-opacity" />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
  );
};

export default Attendance;
